<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { TableSchema } from '../models/TableSchema'
  import { TableField } from '../models/TableField'
  import { TableFieldType } from '../models/TableFieldType'
  import { TablesService } from '../services/TablesService'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import ArrayTableBuilder from './ArrayTableBuilder.vue'
  import JsonTableConfiguration from './JsonTableConfiguration.vue'
  import { TablesJsonTableConfigurationService } from '../services/TablesJsonTableConfigurationService'
  import SelectOrCreateTableConfiguration from './SelectOrCreateTableConfiguration.vue'
  import ColorTableConfiguration from './ColorTableConfiguration.vue'
  import ColumnMultiSelectOrCreateTableConfiguration from './ColumnMultiSelectOrCreateTableConfiguration.vue'
  import TableHelpPopover from '../help/components/TableHelpPopover.vue'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import RegexTablesConfiguration from './RegexTablesConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { TableDataSourceType } from '../models/TableDataSourceType'
  import TableDataSourceSelectConfiguration from './TableDataSourceSelectConfiguration.vue'
  import TableColumnTypeConfiguration from './TableColumnTypeConfiguration.vue'
  import { Table } from '../models/Table'
  import { TablesUiService } from '../services/TablesUiService'
  import { AnalysisWizardModel } from '@/components/AnalysisWizard/models/AnalysisWizardModel'

  interface IObjectTableBuilderProps {
    tableSchemas: Array<TableSchema>
    tableField: TableField
    rootTable: Table | null
    modelValue: any
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    availableDataSources: Array<{
      value: string
      label: string
      id: string
      type: TableDataSourceType
    }>
    selectedDataSource?: { value: string; label: string; id: string; type: TableDataSourceType }
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
    wizardModel?: AnalysisWizardModel
  }

  interface IObjectTableBuilderEmits {
    (e: 'update:modelValue', value: {}): void
    (
      e: 'data-source-selected',
      value: { value: string; label: string; id: string; type: TableDataSourceType }
    ): void
    (e: 'generating-rows', value: boolean): void
    (e: 'row-generation-complete', success: boolean): void
  }

  const props = defineProps<IObjectTableBuilderProps>()
  const emit = defineEmits<IObjectTableBuilderEmits>()

  const formRef = ref()
  const modelValue = reactive<any>(props.modelValue)
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.tableField.field_type !== TableFieldType.Object) return
    // Support nested object structures
    if (
      (props.tableField.item_schema == null && props.tableField.fields == null) ||
      (props.tableField.item_schema != null &&
        props.tableField.item_schema.field_type !== TableFieldType.Object)
    )
      throw new Error(
        'ObjectTableBuilder requires an Object type with fields or child items_schema of type'
      )

    if (props.tableField.fields != null) {
      configuration.value = TablesService.initializeTableFieldConfiguration(
        props.tableField.fields!
      )
    } else if (props.tableField.item_schema != null) {
      configuration.value = TablesService.initializeTableConfigurationForField(
        props.tableField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.tableField) {
      emit('update:modelValue', configuration.value)
    }
  }

  const handleGeneratingRowsChange = (value: boolean) => {
    emit('generating-rows', value)
  }

  const handleGeneratingRowsCompleted = async (success: boolean) => {
    emit('row-generation-complete', success)
  }

  const handleDataSourceSelected = (value: {
    value: string
    label: string
    id: string
    type: TableDataSourceType
  }) => {
    emit('data-source-selected', value)
  }

  // @ts-expect-error
  const handleTableTypeChanged = (tableType: string, value: {}) => {
    //TODO: Support object nested custom tables and type conversion
    //if (props.table) {
    //emit('configuration-updated', { ...props.table, type: tableType }, value, true)
    //}
  }

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string, disableSmartParse?: boolean) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({
      value: newValue,
      field: fieldName,
      disableSmartParse: disableSmartParse ?? false
    })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const inputValueChanged$ = new Subject<{
    field: string
    value: string
    disableSmartParse: boolean
  }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe(({ field, value, disableSmartParse }) => {
      if (!disableSmartParse) {
        const parsedValue = parseSmartValue(value)
        configuration.value[field] = parsedValue
      } else {
        configuration.value[field] = value
      }
      handleConfigurationChange()
    })

  // Watch for table changes
  watch(
    modelValue,
    (newVal) => {
      configuration.value = { ...newVal }
    },
    { immediate: true }
  )

  watch(
    () => props.tableField,
    (newTableField) => {
      if (newTableField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props?.tableField?.fields
      ? props.tableField.fields.filter(
          (f: any) =>
            f.field_type !== TableFieldType.Object &&
            f.field_type !== TableFieldType.Array &&
            f.field_type !== TableFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.tableField?.fields?.findIndex(
        (x: TableField) =>
          x.field_type === TableFieldType.Array ||
          x.field_type === TableFieldType.Object ||
          x.field_type === TableFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: TableField): boolean => {
    return field.field_type !== TableFieldType.Object && field.field_type !== TableFieldType.Json
  }

  const isObjectWithNestedObject = computed(
    () => props.tableField.fields == null && props.tableField.item_schema != null
  )

  const { controlSize } = useWindowSize()
  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.tableField?.display_field as string | string[] | null | undefined
    if (mappedField == null) return undefined

    const fields = props.tableField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-table-builder-grid-span">
    <ObjectTableBuilder
      :configuration-level="props.configurationLevel"
      :root-table="props.rootTable"
      :available-columns="props.availableColumns"
      :available-params="props.availableParams"
      :available-data-sources="props.availableDataSources"
      v-model="configuration[props.tableField.item_schema!.name]"
      :table-field="props.tableField.item_schema!"
      @update:modelValue="handleConfigurationChange"
      @data-source-selected="handleDataSourceSelected"
      :selected-data-source="selectedDataSource"
      :table-schemas="tableSchemas"
      :is-condensed-layout="props.isCondensedLayout"
      :global-collapsed="globalCollapsed"
      :wizard-model="wizardModel"
    />
  </div>

  <div v-else>
    <div
      v-if="tableField"
      :class="[
        'border',
        'table-configuration',
        'h-full',
        'w-full',
        {
          'child-table-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <!-- Table Header -->
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ tableField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  :outlined="false"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <ExpandOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <CompressOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <!-- Configuration Form -->
        <div
          :class="['flex-1', 'overflow-y-auto', { 'collapsed-table-configuration': isCollapsed }]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div :class="{ 'object-table-fields-grid': true, expanded: !props.isCondensedLayout }">
              <div
                v-for="field in tableField.fields"
                :key="field.name"
                class="object-table-form-field-grid"
                :class="{
                  'array-table-builder-grid-span': field.field_type === TableFieldType.Array,
                  'object-table-builder-grid-span': field.field_type === TableFieldType.Object,
                  'json-table-configuration-grid-span': field.field_type === TableFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== TableFieldType.Object &&
                    field.field_type !== TableFieldType.Array &&
                    field.field_type !== TableFieldType.Json,
                  '!hidden': !TablesUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootTable,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="table-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="table-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>

                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <TableHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>

                <!-- Text Input -->
                <Input
                  v-if="field.field_type === TableFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="
                    (val: string) => onInputChange(val, field.name, field.disable_smart_parse)
                  "
                  @blur="() => onInputBlur(field.name)"
                  :data-field="`${props.configurationLevel}:${field.name}`"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === TableFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                />

                <!-- Regex Input -->
                <RegexTablesConfiguration
                  v-else-if="field.field_type === TableFieldType.RegularExpression"
                  v-model="configuration[field.name]"
                  :required="field.required"
                  :regex="field.field_type_args?.regex_pattern"
                  :data-type="field.field_type_args?.regex_data_type"
                  :placeholder="field.placeholder || ''"
                  :error-message="field.field_type_args?.regex_description"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === TableFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Select Dropdown JSON Payload -->
                <Select
                  v-else-if="field.field_type === TableFieldType.SelectJson"
                  :value="JSON.stringify(configuration[field.name])"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @update:value="
                    (val: string) => {
                      try {
                        const parsed = JSON.parse(val)
                        configuration[field.name] = parsed
                        handleConfigurationChange()
                      } catch (e) {
                        console.error('Failed to parse selected JSON value', e)
                      }
                    }
                  "
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="JSON.stringify(option.value)"
                    :value="JSON.stringify(option.value)"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreateTableConfiguration
                  v-else-if="field.field_type === TableFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options || []"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="
                    field.field_type === TableFieldType.MultiSelect ||
                    field.field_type === TableFieldType.ColumnMultiSelect ||
                    field.field_type === TableFieldType.ParamMultiSelect ||
                    field.field_type === TableFieldType.ParamOrColumnMultiSelect
                  "
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === TableFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === TableFieldType.ColumnMultiSelect"
                    v-for="option in props.availableColumns"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === TableFieldType.ParamMultiSelect"
                    v-for="option in props.availableParams"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === TableFieldType.ParamOrColumnMultiSelect"
                    v-for="option in [...props.availableColumns, ...props.availableParams]"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === TableFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Column Select -->
                <Select
                  v-else-if="field.field_type === TableFieldType.ColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableColumns"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Param Select -->
                <Select
                  v-else-if="field.field_type === TableFieldType.ParamSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableParams"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Param or Column Select -->
                <Select
                  v-else-if="field.field_type === TableFieldType.ParamOrColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in [...props.availableParams, ...props.availableColumns]"
                    :key="column.value"
                    :value="column.value"
                  >
                    {{ column.label }}
                  </SelectOption>
                </Select>

                <!-- Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === TableFieldType.ColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableColumns"
                  :placeholder="
                    field.placeholder || `Select existing column or enter new column name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Param Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === TableFieldType.ParamSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableParams"
                  :placeholder="
                    field.placeholder || `Select existing param or enter new param name`
                  "
                  :size="controlSize"
                  class="w-full"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Param or Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === TableFieldType.ParamOrColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="[...props.availableColumns, ...props.availableParams]"
                  :placeholder="
                    field.placeholder || `Select existing param or column or enter new value`
                  "
                  :size="controlSize"
                  class="w-full"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Multi Column Select Or Create -->
                <ColumnMultiSelectOrCreateTableConfiguration
                  v-else-if="field.field_type === TableFieldType.ColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableColumns"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi Param Select Or Create -->
                <ColumnMultiSelectOrCreateTableConfiguration
                  v-else-if="field.field_type === TableFieldType.ParamMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableParams"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Param or Column Column Multi Select Or Create -->
                <ColumnMultiSelectOrCreateTableConfiguration
                  v-else-if="field.field_type === TableFieldType.ParamOrColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="[...props.availableColumns, ...props.availableParams]"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Color Picker -->
                <ColorTableConfiguration
                  v-else-if="field.field_type === TableFieldType.ColorPicker"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Select color'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select DataSource -->
                <TableDataSourceSelectConfiguration
                  v-else-if="field.field_type === TableFieldType.SelectDataSource"
                  v-model="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()} value`"
                  :available-data-sources="props.availableDataSources"
                  :data-source-types="field.field_type_args.datasource_types"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                  @data-source-selected="handleDataSourceSelected"
                />

                <!-- Array Type -->
                <ArrayTableBuilder
                  ref="arrayBuilderRef"
                  v-else-if="field.field_type === TableFieldType.Array"
                  :configuration-level="props.configurationLevel"
                  :root-table="props.rootTable"
                  :available-columns="props.availableColumns"
                  :available-params="props.availableParams"
                  :available-data-sources="props.availableDataSources"
                  v-model="configuration[field.name]"
                  :table-field="field"
                  @update:modelValue="handleConfigurationChange"
                  @data-source-selected="handleDataSourceSelected"
                  @generating-rows="handleGeneratingRowsChange"
                  @row-generation-complete="handleGeneratingRowsCompleted"
                  :selected-data-source="selectedDataSource"
                  :table-schemas="tableSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                  :wizard-model="wizardModel"
                />

                <!-- Object Type (nested) -->
                <ObjectTableBuilder
                  v-else-if="field.field_type === TableFieldType.Object"
                  :configuration-level="props.configurationLevel + 1"
                  :root-table="props.rootTable"
                  :available-columns="props.availableColumns"
                  :available-params="props.availableParams"
                  :available-data-sources="props.availableDataSources"
                  v-model="configuration[field.name]"
                  :table-field="field"
                  @update:modelValue="handleConfigurationChange"
                  @data-source-selected="handleDataSourceSelected"
                  :selected-data-source="selectedDataSource"
                  :table-schemas="tableSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                  :wizard-model="wizardModel"
                />

                <!-- String Template -->
                <StringTemplateBuilder
                  v-else-if="field.field_type === TableFieldType.StringTemplate"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :delimiter_start="field.field_type_args?.delimiter_start"
                  :delimiter_end="field.field_type_args?.delimiter_end"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder"
                  @update:modelValue="handleConfigurationChange"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Table Column Type Select -->
                <TableColumnTypeConfiguration
                  v-else-if="field.field_type === TableFieldType.TableColumnTypeSelect"
                  :table-schemas="tableSchemas"
                  v-model="configuration[field.name]"
                  :configuration="configuration"
                  :configured-field="field"
                  :selected-data-source="selectedDataSource"
                  :configuration-level="props.configurationLevel + 1"
                  @update:modelValue="handleConfigurationChange"
                  @generating-rows="handleGeneratingRowsChange"
                  @row-generation-complete="handleGeneratingRowsCompleted"
                  :is-condensed-layout="
                    props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                  "
                  @configuration-updated="
                    (cfg) => {
                      if (!cfg) return
                      configuration = { ...(configuration || {}), ...cfg }
                      handleConfigurationChange()
                    }
                  "
                  :global-collapsed="globalCollapsed"
                  :wizard-model="wizardModel"
                />

                <!-- Json Type (Custom Table) -->
                <JsonTableConfiguration
                  v-else-if="field.field_type === TableFieldType.Json"
                  :table-schemas="props.tableSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration"
                  :editor-schema="TablesJsonTableConfigurationService.getJsonEditorSchemaForTable()"
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @table-type-changed="handleTableTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                />

                <!-- Default fallback for unknown field types -->
                <JsonTableConfiguration
                  v-else
                  :table-schemas="props.tableSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    TablesJsonTableConfigurationService.getJsonEditorSchemaForField(field)
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .collapsed-table-configuration {
    display: none;
  }

  .table-configuration {
    border: 1px solid #0000002e;
    padding: 10px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .object-table-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-table-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-table-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-table-builder-grid-span,
  .object-table-builder-grid-span,
  .json-table-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }
</style>
